import {
  addRealNameAuth,
  getUserInfo,
  upload,
  uploadLargeVideo,
} from "@/apis/user";
import { useConfigContext } from "@/app";
import { DialogLocal } from "@/components/Dialog";
import { checkIDCard } from "@/libs/validator";
import { useUserInfoStore } from "@/stores/userInfo";
import { blobToBase64 } from "@/utils/file";
import tools, { dateFormat } from "@/utils/tool";
import { useLockFn } from "ahooks";
import { AutoCenter, Form, Toast } from "antd-mobile";
import MD5 from "crypto-js/md5";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type UploadInfo = {
  imageUrl: string;
  token: string;
  uploadUrl: string;
};
type FileInfo = {
  file: File;
  content: string;
};
type LogicData = {
  isVerifying: boolean;
  showSuccessTip: boolean;
  idCardBackImg: string[];
  isFaceCollecting: boolean;
  featureCount: number;
  uploadInfo: UploadInfo;
  showUploadProgress: boolean;
};

const initialData = {
  isVerifying: false,
  showSuccessTip: false,
  idCardBackImg: [],
  isFaceCollecting: false,
  featureCount: 0,
  uploadInfo: {
    imageUrl: "",
    token: "",
    uploadUrl: "",
  },
  showUploadProgress: false,
};

export const allSteps = [
  {step: 1, text: '上传身份证', icon: 'id-card'},
  {step: 2, text: '人脸识别', icon: 'scan-face'},
  {step: 3, text: '人工审核', icon: 'approval'},
]

export const useLogic = () => {
  const [logicData, setLogicData] = useState<LogicData>({ ...initialData });
  const { config,...datas } = useConfigContext();
  console.log(config,datas);//datas.isH5Plus
  //上传到第几
  const currentRateRef = useRef<number>(0);
  //第几步
  const stepRef = useRef<number>(0);

  //倒计时
  const timeRef = useRef<number>(0);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [auditRealNameStatus, setAuditRealNameStatus] = useState<number | "">(
    0
  );
  //人脸数字
  const [code, setCode] = useState<number[]>([0, 0, 0, 0]);
  const [userInfo, setUserInfo] = useUserInfoStore()((state) => [
    state.userInfo,
    state.setUserInfo,
  ]);

  const steps = useMemo(() => {
    if(config?.isFaceVerify === '1'){
      return allSteps;
    }else{
      return allSteps.filter( step => step.step !== 2)
    }
  }, [config?.isFaceVerify])

  const isH5Plus: boolean = useMemo(() => {
    //@ts-ignore
    return config && config.isH5Plus;//datas.isH5Plus
  }, [config]);
  
  const isVerifying: boolean = useMemo(() => {
    return +auditRealNameStatus > 0;
  }, [auditRealNameStatus]);

  const fetchUserInfo = async () => {
    const response = await getUserInfo({});
    if (response.ok && response.data) {
      setUserInfo(response.data);
    }
    return response;
  };

  const countdownCode = () => {
    setLogicData({
      ...logicData,
    });
    timeRef.current = 1000 * 60 * 5;
    setCode([0, 0, 0, 0].map(() => Math.floor(Math.random() * 10)));
  };
  const finish = () => {
    setCode([0, 0, 0, 0].map(() => Math.floor(Math.random() * 10)));

    // this.$refs.countdown.reset()
  };

  const submit = (faceImg: string) => {
    const values = form.getFieldsValue();
    const data: any = {};
    data.code = code.join("");
    data.idCard = values.idCard;
    data.idCardFrontImg = values.idCardFrontImg?.key || "";
    data.idCardReverseImg = values.idCardReverseImg?.key || "";
    data.faceImg = faceImg;
    data.realName = values.realName;

    addRealNameAuth(data).then(({ ok, msg }) => {
      if (ok) {
        setUserInfo({
          auditRealNameStatus: 0,
        });
        setLogicData({
          ...logicData,
          showSuccessTip: true,
        });
        DialogLocal({
          title: "提交成功",
          content: (
            <AutoCenter>
              人工审核需要3-10分钟审核结果在「用户中心-实名认证」中查看
            </AutoCenter>
          ),
          onConfirm: () => {
            navigate(-1);
          },
        });
      } else {
        Toast.show(msg);
      }
    });
  };

  const finishUpload = () => {
    setLogicData({
      ...logicData,
      showUploadProgress: false,
    });
    currentRateRef.current = 0;
  };

  const fetchUploadLargeVideo = (file: FileInfo, file_name: string) => {
    const [, content] = (file.content || "").split(",");
    // const mime = info.split(";")[0].split(":")[1];
    const chunks: string[] = [];
    const chunk_size = 2 * 1000 * 1024;
    let chunk_len = 100 / chunks.length;
    let index = 0;

    for (; index * chunk_size < content.length; ) {
      index++;
      chunks.push(content.slice((index - 1) * chunk_size, index * chunk_size));
    }
    chunk_len = 100 / chunks.length;

    let uploadIndex = 0;
    let fileName = file_name;

    const upload = () => {
      if (uploadIndex >= chunks.length) return;
      const uploadData = {
        videoBase64: chunks[uploadIndex],
        blockOrder: uploadIndex + 1,
        blockNum: chunks.length,
        fileName,
      };
      const uploadOpt = {
        onUploadProgress: (event: { loaded: number; total: number }) => {
          currentRateRef.current =
            chunk_len * uploadIndex + (event.loaded / event.total) * chunk_len;
        },
      };
      uploadLargeVideo(uploadData, uploadOpt).then(({ ok, data }) => {
        if (ok) {
          currentRateRef.current = ((uploadIndex + 1) / chunks.length) * 100;

          if (data.feof) {
            // 上传完毕
            submit(data.fileName);
            finishUpload();

            Toast.show("上传成功");
          } else {
            fileName = data.nextFileName;
            uploadIndex++;
            upload();
          }
        } else {
          // 上传失败
          finishUpload();
          Toast.show("上传失败");
        }
      });
    };
    // this.showUploadProgress = true;
    setLogicData({
      ...logicData,
      showUploadProgress: true,
    });
    upload();
  };

  const readVideo = async (file: File) => {
    if (file.size >= 40 * 1024 * 1024) {
      Toast.show("文件大小不能超过40M");
    } else {
      const content: string = await blobToBase64(file);
      const time = dateFormat(new Date(), "YMDhms");
      const filename =
        MD5(time + file.name) + `.${file.name.split(".").pop() || "unknown"}`;

      // if (isH5Plus) {
      fetchUploadLargeVideo(
        {
          file,
          content,
        },
        filename
      );
      // } else {
      //   ploadFileByWcs(file.file);
      // }
    }
  };

  const readImage = (file: any, info: { name: "id" | "idBack" }) => {
    file.status = "uploading";
    file.message = "上传中...";
    upload({ imgFile: file.file, type: "authImg" }).then(
      ({ ok, data, msg }) => {
        if (ok) {
          file.status = "done";
          file.url = data.name;
          if (info.name === "id") {
            form.setFieldsValue({
              idCardFrontImg: data.name,
            });
          }
          if (info.name === "idBack") {
            form.setFieldsValue({
              idCardReverseImg: data.name,
            });
          }
        } else {
          file.status = "error";
          file.message = "上传失败";
          Toast.show(msg);
        }
      }
    );
  };

  const uploadID = (e: Event, info: { name: "id" | "idBack" }) => {
    if (isH5Plus) {
      e.preventDefault();
      // @ts-ignore
      window.h5p.uploadImage((blob) => {
        if (blob) {
          readImage({ file: blob }, info);
        } else {
          Toast.show("获取图片失败");
        }
      }, true);
    }
  };

  const onNext = () => {
    const values2 = form.getFieldsValue();
    console.log('values.idCardFrontImg==',values2.idCardFrontImg);
    console.log('values.idCardReverseImg==',values2.idCardReverseImg);
    console.log(` values.idCardFrontImg stringify==${JSON.stringify(values2.idCardFrontImg)}`);
    console.log(` values.idCardReverseImg stringify==${JSON.stringify(values2.idCardReverseImg)}`);
    if( !userInfo.phone ){
      return DialogLocal({
        cancelText: "取消",
        content: <AutoCenter>请先绑定手机号后进行实名验证！</AutoCenter>,
        confirmText: "去绑定",
        onConfirm() {
          navigate(`/user_phone`);
        },
      });
    } 
    
    const values = form.getFieldsValue();
    if (!values.realName) return Toast.show("请输入真实用户名");
    if (!values.idCard || !checkIDCard(String(values.idCard).replace(" ", "")))
      return Toast.show("请输入合法身份证号码");
    if (!values.idCardFrontImg) return Toast.show("请上传身份证正面照片");
    if (!values.idCardReverseImg) return Toast.show("请上传身份证反面照片");
    if(config?.isFaceVerify === '1'){
      stepRef.current = stepRef.current += 1;
      countdownCode();
    }else{
      const data: any = {};
      data.code = "";
      data.idCard = values.idCard;
      data.idCardFrontImg = values.idCardFrontImg?.key || "";
      data.idCardReverseImg = values.idCardReverseImg?.key || "";
      data.faceImg = '';
      data.realName = values.realName;
  
      addRealNameAuth(data).then(({ ok, msg }) => {
        if (ok) {
          setUserInfo({
            auditRealNameStatus: 0,
          });
          setLogicData({
            ...logicData,
            showSuccessTip: true,
          });
          DialogLocal({
            title: "提交成功",
            content: (
              <AutoCenter>
                人工审核需要3-10分钟审核结果在「用户中心-实名认证」中查看
              </AutoCenter>
            ),
            onConfirm: () => {
              navigate(-1);
            },
          });
        } else {
          Toast.show(msg);
        }
      });
    }
    
  };
  const onReCertify = () => {
    setAuditRealNameStatus("");
    stepRef.current = 0;
    form.resetFields();
  };
  const onDialogClose = () => {
    navigate(-1);
  };

  const userinfo = useLockFn(async () => {
    const response = await fetchUserInfo();
    const loading = Toast.show("查询审核状态中...");
    loading.close();
    if (response.ok) {
      const auditRealNameStatus = response.data!.auditRealNameStatus;
      if (
        tools.isUndefined(auditRealNameStatus) ||
        String(auditRealNameStatus) === ""
      ) {
        stepRef.current = 0;
      } else if (auditRealNameStatus === 0) {
        stepRef.current = 2;

        DialogLocal({
          content: (
            <AutoCenter>实名认证资料正在人工审核中，请耐心等待</AutoCenter>
          ),
          onConfirm: () => {
            navigate(-1);
          },
        });
      } else if (auditRealNameStatus === 2) {
        stepRef.current = 2;
      }
      setAuditRealNameStatus(auditRealNameStatus || 0);
    } else {
      DialogLocal({
        content: <AutoCenter>查询审核状态失败，请稍后再试</AutoCenter>,
        onConfirm: () => {
          navigate(-1);
        },
      });
    }
  });

  useEffect(() => {
    if (isH5Plus) {
      // 获取权限
      //@ts-ignore
      window.plus.camera.getCamera();
    }
    // 获取wcs上传信息
    userinfo();
  }, []);

  return {
    uploadID,
    isH5Plus,
    onReCertify,
    onDialogClose,
    readVideo,
    fetchUploadLargeVideo,
    logicData,
    currentRate: currentRateRef.current,
    finishUpload,
    finish,
    form,
    onNext, // next step,
    isVerifying,
    code, // vedio code
    time: timeRef.current,
    step: stepRef.current,
    auditRealNameStatus,
    steps
  };
};

// 440102198001021230
// id card add test datarealName: hhhh
// idCard: 440102198001021230
// idCardFrontImg: /Upload/Image/authImg/2023/09/24/fc659559dca2cdfa9c6efbb08948ac20.jpeg
// idCardReverseImg: /Upload/Image/authImg/2023/09/24/e26a6fe28a18db2b8452a73cb4564b2a.jpeg
// faceImg: Upload/Video/authVideo/2023/09/24/0398230949c46503bf69997bcddd11ba.mp4
// code: 8806
// 532018259a154fcde553f4b120e9ef6a
// 10f5be13e127d10608f8024627f32567